import { useState, useCallback, useMemo, useEffect } from 'react';

interface TablePreferencesConfig {
  storageKey: string;
  defaults: {
    pageSize: number;
    visibleContent: string[];
  };
  pageSizeOptions: Array<{ value: number; label: string }>;
  visibleContentOptions: Array<{
    label: string;
    options: Array<{ id: string; label: string; editable?: boolean }>;
  }>;
}

interface StoredPreferences {
  pageSize: number;
  visibleContent: string[];
  columnWidths: Record<string, number>;
}

function loadPreferences(storageKey: string, defaults: TablePreferencesConfig['defaults']): StoredPreferences {
  try {
    const raw = localStorage.getItem(`table-prefs:${storageKey}`);
    if (!raw) return { ...defaults, columnWidths: {} };
    const parsed = JSON.parse(raw);
    return {
      pageSize: typeof parsed.pageSize === 'number' ? parsed.pageSize : defaults.pageSize,
      visibleContent: Array.isArray(parsed.visibleContent) ? parsed.visibleContent : defaults.visibleContent,
      columnWidths: parsed.columnWidths && typeof parsed.columnWidths === 'object' ? parsed.columnWidths : {},
    };
  } catch {
    return { ...defaults, columnWidths: {} };
  }
}

function savePreferences(storageKey: string, prefs: StoredPreferences): void {
  localStorage.setItem(`table-prefs:${storageKey}`, JSON.stringify(prefs));
}

export function useTablePreferences(config: TablePreferencesConfig) {
  const { storageKey, defaults, pageSizeOptions, visibleContentOptions } = config;

  const [prefs, setPrefs] = useState<StoredPreferences>(() => loadPreferences(storageKey, defaults));

  useEffect(() => {
    try {
      savePreferences(storageKey, prefs);
    } catch {
      // localStorage may be full or disabled — silently fail
    }
  }, [storageKey, prefs]);

  const allColumnIds = useMemo(
    () => visibleContentOptions.flatMap((group) => group.options.map((opt) => opt.id)),
    [visibleContentOptions],
  );

  const columnDisplay = useMemo(
    () => allColumnIds.map((id) => ({ id, visible: prefs.visibleContent.includes(id) })),
    [allColumnIds, prefs.visibleContent],
  );

  const handleConfirm = useCallback(
    (event: { detail: { pageSize?: number; visibleContent?: readonly string[] } }) => {
      const { pageSize, visibleContent } = event.detail;
      setPrefs((prev) => ({
        ...prev,
        ...(pageSize != null && { pageSize }),
        ...(visibleContent != null && { visibleContent: [...visibleContent] }),
      }));
    },
    [],
  );

  const handleColumnWidthsChange = useCallback(
    (columnIds: string[], event: { detail: { widths: readonly number[] } }) => {
      const widths = event.detail.widths;
      const columnWidths: Record<string, number> = {};
      columnIds.forEach((id, i) => {
        if (widths[i] != null) columnWidths[id] = widths[i];
      });
      setPrefs((prev) => ({ ...prev, columnWidths: { ...prev.columnWidths, ...columnWidths } }));
    },
    [],
  );

  const getColumnWidth = useCallback(
    (columnId: string, fallback: number): number => {
      return prefs.columnWidths[columnId] ?? fallback;
    },
    [prefs.columnWidths],
  );

  const collectionPreferencesProps = useMemo(() => ({
    preferences: {
      pageSize: prefs.pageSize,
      visibleContent: prefs.visibleContent,
    },
    onConfirm: handleConfirm,
    pageSizePreference: {
      title: 'Page size',
      options: pageSizeOptions,
    },
    visibleContentPreference: {
      title: 'Visible columns',
      options: visibleContentOptions,
    },
  }), [prefs.pageSize, prefs.visibleContent, handleConfirm, pageSizeOptions, visibleContentOptions]);

  return {
    pageSize: prefs.pageSize,
    columnDisplay,
    getColumnWidth,
    handleColumnWidthsChange,
    collectionPreferencesProps,
  };
}
